import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import GeoSelectField from '../shared/GeoSelectField';
import { geoCascade } from '../shared/bdGeo';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import UnderageNoticeDialog from '../shared/UnderageNoticeDialog';
import { isUnderVoterAge } from '../shared/voterAgeRules';
import VoterMigrationPreviewFrame from './VoterMigrationPreviewFrame';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import VoterMigrationFormInfoBox from './VoterMigrationFormInfoBox';
import {
    INITIAL_VOTER_MIGRATION_FORM,
    displayField,
    formatDateField,
    getEmptyVoterMigrationFields,
    getMobileValidationError,
    getVoterMigrationFormSnapshot,
    isVoterMigrationFormComplete,
    normalizeBdPhone,
    normalizeDigits,
    parseDateField,
    saveVoterMigrationFormSnapshot,
} from './voterMigrationFormEngine';
import { downloadVoterMigrationPdf, printVoterMigrationForm } from './generateVoterMigrationPdf';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';

/** Calendar range: old enough for any voter's birth date, and no future days. */
const TODAY = new Date();
const BIRTH_FROM_YEAR = 1900;
const RESIDENCE_FROM_YEAR = 1950;
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-3.5 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {children}
        </div>
    );
}

/** Plain field — no datalist here: the office data is one-off, not reusable. */
function FormInput({
    id,
    name,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    inputMode,
    type = 'text',
    maxLength,
    autoComplete,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                inputMode={inputMode}
                type={type}
                maxLength={maxLength}
                autoComplete={autoComplete}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass}`}
            />
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function VoterMigrationFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_VOTER_MIGRATION_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    // Under-18 birth date: warned about when picked, and again on the way out.
    const [ageNotice, setAgeNotice] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [phoneError, setPhoneError] = useState('');
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getVoterMigrationFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.applicantName) || displayField(next.nid)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveVoterMigrationFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            if (!displayField(value)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const handleDigitsChange = (key, raw, maxLength) => {
        update(key, normalizeDigits(raw, maxLength));
    };

    const handlePhoneChange = (raw) => {
        const digits = normalizeBdPhone(raw);
        update('phone', digits);
        if (phoneTouched) {
            setPhoneError(getMobileValidationError(digits, t));
        }
    };

    const validatePhone = () => {
        setPhoneTouched(true);
        if (!form.phone) {
            setPhoneError('');
            return true;
        }
        const err = getMobileValidationError(form.phone, t);
        setPhoneError(err);
        return !err;
    };

    const phoneInputError = fieldErrors.phone || (phoneTouched ? phoneError : '');

    /**
     * Remember what was typed, then export — whatever state the form is in.
     *
     * Missing fields deliberately do NOT block the download: a common use of
     * this tool is printing the form blank, or half-filled, to complete the rest
     * by hand at the office. Downloading one only asks for a confirmation that
     * names what is still empty, and printing does not even ask.
     */
    const prepareForExport = () => {
        setFieldErrors({});
        saveVoterMigrationFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyVoterMigrationFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('voterMigrationForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage('general/voter-migration-form', { category: 'general' });
            const base = form.applicantName.trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadVoterMigrationPdf(previewRef.current, `voter-migration-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('voterMigrationForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('voterMigrationForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const birthDate = parseDateField(form.birthDate);
    const underVoterAge = isUnderVoterAge(birthDate);

    const handleDownloadPdf = () => {
        if (underVoterAge) {
            setAgeNotice({ mode: 'download', date: birthDate });
            return;
        }
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = async () => {
        // Printing is an export too — the same under-18 word of warning applies.
        if (underVoterAge) {
            setAgeNotice({ mode: 'print', date: birthDate });
            return;
        }
        await runPrint();
    };

    const runPrint = async () => {
        recordToolUsage('general/voter-migration-form', { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('voterMigrationForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printVoterMigrationForm(previewRef.current, t('voterMigrationForm.errors.popupBlocked'));
            notifications.show({
                title: t('voterMigrationForm.printTitle'),
                message: t('voterMigrationForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('voterMigrationForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_VOTER_MIGRATION_FORM);
        setPhoneError('');
        setPhoneTouched(false);
        setFieldErrors({});
        notifications.show({
            title: t('voterMigrationForm.clearedTitle'),
            message: t('voterMigrationForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getVoterMigrationFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('voterMigrationForm.noSnapshotTitle'),
                message: t('voterMigrationForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        setPhoneError('');
        setPhoneTouched(false);
        setFieldErrors({});
        notifications.show({
            title: t('voterMigrationForm.restoredTitle'),
            message: t('voterMigrationForm.restoredMessage'),
            color: 'green',
        });
    };

    const hasAnyInput = useMemo(
        () => Object.values(form).some((value) => displayField(value)),
        [form],
    );

    const isComplete = useMemo(() => isVoterMigrationFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('voterMigrationForm.statusReady'),
        success: t('voterMigrationForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`voterMigrationForm.howTo.step${i}`)),
        [t],
    );

    const fieldError = (key) => {
        const err = fieldErrors[key];
        if (!err) return undefined;
        if (key === 'phone') return err;
        return ' ';
    };

    /**
     * Division / district / upazila as linked dropdowns (see shared/bdGeo): the
     * first two pick from the list, upazila also takes a typed name.
     */
    const geoField = (key, geo, { selectOnly = false } = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterMigrationForm.fields.${key}`)}</FormLabel>
            <GeoSelectField
                {...geo}
                selectOnly={selectOnly}
                placeholder={t(`voterMigrationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputClassName={`${inputBaseClass} ${fieldError(key) ? inputErrClass : inputOkClass}`}
            />
        </div>
    );

    // District comes first in each block so the upazila list can follow it.
    const officeGeo = geoCascade(form, update, { district: 'officeDistrict', upazila: 'officeUpazila' });
    const currentGeo = geoCascade(form, update, { district: 'currentDistrict', upazila: 'currentUpazila' });
    const newGeo = geoCascade(form, update, { district: 'newDistrict', upazila: 'newUpazila' });

    /** Every plain text field is wired the same way. */
    const textField = (key, extra = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterMigrationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => update(key, v)}
                placeholder={t(`voterMigrationForm.placeholders.${key}`)}
                error={fieldError(key)}
                {...extra}
            />
        </div>
    );

    /**
     * Dates are picked, not typed: the form wants দিন-মাস-বছর in Bengali
     * numerals, and a picker is the only way that shape comes out right every
     * time. No day after today makes sense on this form — neither a birth date
     * nor the date someone started living at an address.
     */
    const dateField = (key, { fromYear = RESIDENCE_FROM_YEAR } = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterMigrationForm.fields.${key}`)}</FormLabel>
            <DatePickerField
                value={parseDateField(form[key])}
                onChange={(date) => {
                    update(key, formatDateField(date));
                    if (key === 'birthDate' && isUnderVoterAge(date)) setAgeNotice({ mode: 'picked', date });
                }}
                formatValue={formatDateField}
                placeholder={t(`voterMigrationForm.placeholders.${key}`)}
                triggerClassName={`${inputBaseClass} ${fieldError(key) ? inputErrClass : inputOkClass}`}
                fromYear={fromYear}
                toYear={TODAY.getFullYear()}
                disabled={{ after: TODAY }}
                clearable
            />
        </div>
    );

    const digitField = (key, maxLength, extra = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterMigrationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => handleDigitsChange(key, v, maxLength)}
                placeholder={t(`voterMigrationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="numeric"
                maxLength={maxLength}
                {...extra}
            />
        </div>
    );

    return (
        <>
        <UnderageNoticeDialog
            open={Boolean(ageNotice)}
            birthDate={ageNotice?.date ?? null}
            confirmLabel={ageNotice?.mode === 'download'
                ? t('underageNotice.confirmDownload')
                : ageNotice?.mode === 'print' ? t('underageNotice.confirmPrint') : undefined}
            onCancel={() => setAgeNotice(null)}
            onConfirm={() => {
                const mode = ageNotice?.mode;
                setAgeNotice(null);
                if (mode === 'print') { void runPrint(); return; }
                if (mode !== 'download') return;
                if (emptyFields.length) setConfirmingDownload(true);
                else runDownloadPdf();
            }}
        />
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug="general/voter-migration-form"
            subtitleKey="voterMigrationForm.subtitle"
            // 30/70: the sheet is what the applicant is checking as they type, and
            // an A4 page needs the room to stay legible, so the fields take the
            // narrow column and the preview becomes the sticky rail beside them.
            // The 9px is half the column gap.
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('voterMigrationForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('voterMigrationForm.clearAll')}
            primaryAction={{
                label: t('voterMigrationForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            // Print and Restore ride in the sticky bar with Download and Clear,
            // so every action on the form is in one place at the foot of the
            // screen rather than parked beside a column you have scrolled past.
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('voterMigrationForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('voterMigrationForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection
                        number={t('voterMigrationForm.sections.office.number')}
                        title={t('voterMigrationForm.sections.office.title')}
                    >
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {geoField('officeDistrict', officeGeo.district, { selectOnly: true })}
                            {geoField('officeUpazila', officeGeo.upazila)}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterMigrationForm.sections.applicant.number')}
                        title={t('voterMigrationForm.sections.applicant.title')}
                    >
                        <div className="space-y-3">
                            {textField('applicantName', { autoComplete: 'name' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {digitField('nid', 17, { autoComplete: 'national-id' })}
                                {dateField('birthDate', { fromYear: BIRTH_FROM_YEAR })}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterMigrationForm.sections.current.number')}
                        title={t('voterMigrationForm.sections.current.title')}
                    >
                        <div className="space-y-3">
                            {digitField('currentVoterNo', 20)}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('currentAreaName')}
                                {digitField('currentAreaNo', 10)}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {geoField('currentDistrict', currentGeo.district, { selectOnly: true })}
                                {geoField('currentUpazila', currentGeo.upazila)}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('currentVillage', { autoComplete: 'street-address' })}
                                {textField('currentHolding')}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterMigrationForm.sections.destination.number')}
                        title={t('voterMigrationForm.sections.destination.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {geoField('newDistrict', newGeo.district, { selectOnly: true })}
                                {geoField('newUpazila', newGeo.upazila)}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('newLocalBody')}
                                {digitField('newWardNo', 3)}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('newAreaName')}
                                {digitField('newAreaNo', 10)}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('newVillage', { autoComplete: 'street-address' })}
                                {textField('newHolding')}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="phone">{t('voterMigrationForm.fields.phone')}</FormLabel>
                                    <FormInput
                                        id="phone"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handlePhoneChange}
                                        onBlur={validatePhone}
                                        placeholder={t('voterMigrationForm.placeholders.phone')}
                                        error={phoneInputError}
                                        inputMode="tel"
                                        type="tel"
                                        maxLength={11}
                                        autoComplete="tel"
                                    />
                                </div>
                                {textField('postOffice', { autoComplete: 'address-line2' })}
                            </div>
                            {digitField('postCode', 4, { autoComplete: 'postal-code' })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterMigrationForm.sections.reason.number')}
                        title={t('voterMigrationForm.sections.reason.title')}
                    >
                        <div className="space-y-3">
                            {dateField('stayingSince')}
                            {textField('migrationReason')}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterMigrationForm.sections.identifier.number')}
                        title={t('voterMigrationForm.sections.identifier.title')}
                    >
                        <div className="space-y-3">
                            {textField('identifierName', { autoComplete: 'name' })}
                            {digitField('identifierNid', 17)}
                            {textField('identifierAddress', { autoComplete: 'street-address' })}
                        </div>
                    </FormSection>
                </div>
            )}
            preview={(
                // A flex column, not a plain block: the preview panel stretches
                // itself, and inside a block wrapper that height would resolve
                // against nothing.
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('voterMigrationForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('voterMigrationForm.previewEmpty')}
                        idleHint={t('voterMigrationForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        {/* Bounded, not min-height: a card taller than the screen
                            cannot stay pinned, so the pages scroll inside it. It
                            takes the whole viewport bar the chrome above and the
                            action bar below, so the rail is not left half empty
                            beside a long form. */}
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('voterMigrationForm.livePreview')}
                            </div>
                            <VoterMigrationPreviewFrame form={form} previewRef={previewRef} zoom={pageZoom.zoom} />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<VoterMigrationFormInfoBox />}
        />
        </>
    );
}
